/**
 * Countdown hero — pre-match state for the football-team adaptive hero.
 *
 * Runs a 1-second countdown to kickoff, driven by an initial match snapshot
 * from the server. When the countdown reaches zero the display flips to
 * "Match starting" — the next server render will promote to the live state.
 */

"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import type { FUSportsMatch } from "fansunited-data-layer";

interface CountdownHeroProps {
    match: FUSportsMatch;
    matchHref: string;
    kickoffDateLabel?: string;
    matchStartingLabel: string;
    kicksOffInLabel: string;
    matchCentreLabel: string;
    daysLabel: string;
    hoursLabel: string;
    minutesLabel: string;
    secondsLabel: string;
}

function computeTimeParts(kickoff: Date): { days: number; hours: number; minutes: number; seconds: number; done: boolean } {
    const now = new Date();
    const diff = kickoff.getTime() - now.getTime();
    if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, done: true };
    }
    const totalSeconds = Math.floor(diff / 1000);
    return {
        days: Math.floor(totalSeconds / (24 * 3600)),
        hours: Math.floor((totalSeconds % (24 * 3600)) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        done: false,
    };
}

function pad(n: number): string {
    return n < 10 ? `0${n}` : String(n);
}

export function CountdownHero({
    match,
    matchHref,
    kickoffDateLabel,
    matchStartingLabel,
    kicksOffInLabel,
    matchCentreLabel,
    daysLabel,
    hoursLabel,
    minutesLabel,
    secondsLabel,
}: CountdownHeroProps) {
    const kickoffRaw = match.timing?.kickoffTime;
    const kickoff = kickoffRaw instanceof Date ? kickoffRaw : kickoffRaw ? new Date(kickoffRaw) : null;
    const [parts, setParts] = useState(() =>
        kickoff ? computeTimeParts(kickoff) : { days: 0, hours: 0, minutes: 0, seconds: 0, done: true }
    );

    useEffect(() => {
        if (!kickoff) return;
        const id = setInterval(() => setParts(computeTimeParts(kickoff)), 1000);
        return () => clearInterval(id);
    }, [kickoff?.getTime()]);

    const home = match.competitorOne;
    const away = match.competitorTwo;

    return (
        <section
            data-hero-state="countdown"
            className="relative w-full h-[420px] md:h-[520px] rounded-lg overflow-hidden bg-gradient-to-br from-primary to-primary/80 text-white flex flex-col justify-between p-6 md:p-10"
        >
            <div className="flex items-center gap-2">
                {match.competition?.name && (
                    <span className="text-xs uppercase tracking-widest opacity-80">{match.competition.name}</span>
                )}
                {kickoffDateLabel && (
                    <span className="text-xs uppercase tracking-widest opacity-80">· {kickoffDateLabel}</span>
                )}
            </div>

            <div className="flex items-center justify-around">
                <div className="flex flex-col items-center gap-3 flex-1">
                    {home.assets?.logo && <img src={home.assets.logo} alt={home.name} className="h-16 md:h-24 w-auto" />}
                    <div className="text-base md:text-xl font-semibold text-center">{home.name}</div>
                </div>
                <div className="text-3xl md:text-5xl font-extrabold opacity-70 px-6">VS</div>
                <div className="flex flex-col items-center gap-3 flex-1">
                    {away.assets?.logo && <img src={away.assets.logo} alt={away.name} className="h-16 md:h-24 w-auto" />}
                    <div className="text-base md:text-xl font-semibold text-center">{away.name}</div>
                </div>
            </div>

            <div>
                <div className="text-xs uppercase tracking-widest opacity-80 mb-2 text-center">
                    {parts.done ? matchStartingLabel : kicksOffInLabel}
                </div>
                {!parts.done && (
                    <div className="flex items-end justify-center gap-4 md:gap-6 tabular-nums font-extrabold">
                        <TimeCell value={parts.days} label={daysLabel} />
                        <div className="text-3xl md:text-5xl opacity-40 pb-4 md:pb-6">:</div>
                        <TimeCell value={parts.hours} label={hoursLabel} />
                        <div className="text-3xl md:text-5xl opacity-40 pb-4 md:pb-6">:</div>
                        <TimeCell value={parts.minutes} label={minutesLabel} />
                        <div className="text-3xl md:text-5xl opacity-40 pb-4 md:pb-6">:</div>
                        <TimeCell value={parts.seconds} label={secondsLabel} />
                    </div>
                )}
                <div className="mt-4 flex justify-center">
                    <Link
                        href={matchHref}
                        prefetch={false}
                        className="inline-flex items-center px-4 py-2 rounded-md bg-white/15 hover:bg-white/25 text-sm font-semibold uppercase tracking-wide backdrop-blur-sm"
                    >
                        {matchCentreLabel} →
                    </Link>
                </div>
            </div>
        </section>
    );
}

function TimeCell({ value, label }: { value: number; label: string }) {
    return (
        <div className="flex flex-col items-center">
            <div className="text-4xl md:text-6xl leading-none">{pad(value)}</div>
            <div className="text-[10px] md:text-xs uppercase tracking-widest opacity-70 mt-1">{label}</div>
        </div>
    );
}
